import { CheckCircle2, ScanLine, X } from "lucide-react";
import { useRef, useState, type DragEvent, type FC } from "react";

import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";

import { ForeignNationalsAPI } from "../../../api/foreignNationals";
import type { PassportScan } from "../../../api/types/clientDirectory";

type Props = {
  scanned: { file: File } | null;
  onScanned: (scan: PassportScan, file: File) => void;
  onClear: () => void;
};

const ACCEPT = "image/*,application/pdf";

export const PassportScanDrop: FC<Props> = ({
  scanned,
  onScanned,
  onClear
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [reading, setReading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);

  const read = async (file: File) => {
    setReading(true);
    setError(null);
    try {
      onScanned(await ForeignNationalsAPI.scanPassport(file), file);
    } catch (e) {
      setError(
        e instanceof Error && e.message
          ? e.message
          : "We couldn't read that passport."
      );
    } finally {
      setReading(false);
    }
  };

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) void read(file);
  };

  if (scanned && !reading) {
    return (
      <div
        className="flex items-center gap-3 rounded-lg bg-fill-1 py-2 pr-2 pl-3"
        data-testid="passport-scan-done"
      >
        <CheckCircle2 className="size-4 shrink-0 text-success" />
        <span className="min-w-0 flex-1 text-sm text-fg-2">
          Filled from{" "}
          <span className="font-medium text-fg">{scanned.file.name}</span>.
          Check the details below; the scan is saved to their documents.
        </span>
        <Button
          type="button"
          size="icon-xs"
          variant="ghost"
          aria-label="Remove passport scan"
          onClick={onClear}
        >
          <X />
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1.5">
      <div
        role="button"
        tabIndex={0}
        aria-label="Start from a passport scan"
        aria-busy={reading}
        onClick={() => !reading && inputRef.current?.click()}
        onKeyDown={(e) => {
          if ((e.key === "Enter" || e.key === " ") && !reading) {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        className={cn(
          "flex cursor-pointer items-center gap-3 rounded-lg border border-dashed border-line-3 px-3 py-2.5 transition-colors hover:bg-fill-1 focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:outline-none",
          dragging && "border-fg-3 bg-fill-1"
        )}
      >
        <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-fill-2 text-fg-2">
          {reading ? <Spinner /> : <ScanLine className="size-4" />}
        </span>
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="text-base font-medium text-fg">
            {reading ? "Reading the Passport…" : "Start From a Passport Scan"}
          </span>
          <span className="text-sm text-fg-3">
            Drop the photo page. We read the name, birth date, citizenship and
            passport number for you.
          </span>
        </span>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          className="hidden"
          data-testid="passport-scan-input"
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = "";
            if (file) void read(file);
          }}
        />
      </div>
      {error ? (
        <p className="text-sm text-danger" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
};
